/**
 * Project queries + mutations.
 *
 * Covers single-project read, create/patch, close/reopen, and the department
 * sub-resource. Sub-resources (parts, batches, BGs) get their own files in
 * later passes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './api-client';
import type { ProjectLifecycleState } from './visibility-queries';

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

export type ProjectCurrencyCode = 'PKR' | 'USD' | 'OTHER';
export type ProjectInspectionLocation = 'OUR_PREMISES' | 'CLIENT_PREMISES' | 'THIRD_PARTY_PREMISES' | 'TBD';
export type ProjectTermsOfDelivery = string;

export interface Project {
  id: string;
  projectTitle: string;
  projectNumber: string;
  clientId: string;
  projectCurrency: ProjectCurrencyCode;
  otherCurrencyCode: string | null;
  principalName: string | null;
  principalAddress: string | null;
  principalContactPerson: string | null;
  principalEmail: string | null;
  principalPhone: string | null;
  principalFax: string | null;
  portOfShipment: string | null;
  principalBankName: string | null;
  principalBankAccountNumber: string | null;
  principalBankIban: string | null;
  principalBankAccountTitle: string | null;
  principalBankSwiftCode: string | null;
  principalBankAddress: string | null;
  principalBankEmail: string | null;
  principalBankPhone: string | null;
  principalBankFax: string | null;
  dfaReceivedDate: string;
  dfaSubmittedDate: string | null;
  contractSignDate: string | null;
  dateOfIssuance: string | null;
  technicalBidOpeningDate: string | null;
  financialBidOpeningDate: string | null;
  deliveryDurationMonths: number | null;
  effectiveDeliveryTargetDate: string | null;
  gracePeriodDays: number;
  origin: string | null;
  indentNumber: string | null;
  indentDate: string | null;
  makerNameBrand: string | null;
  totalStoreValuePaisa: number | null;
  gstRateBps: number | null;
  gstAmountPaisa: number | null;
  declaredTotalContractValuePaisa: number | null;
  requiredBgAmountPaisa: number | null;
  bgSubmissionDurationDays: number | null;
  bgValidityDurationMonths: number | null;
  termsOfDelivery: ProjectTermsOfDelivery | null;
  paymentOnINoteBps: number | null;
  paymentOnCrvBps: number | null;
  partialDeliveryAllowed: boolean;
  partialPaymentAllowed: boolean;
  partialDeliveryPaymentRemarks: string | null;
  earnestMoneyAmountPaisa: number | null;
  earnestSubmittedDate: string | null;
  earnestReleasedDate: string | null;
  warrantyPeriodMonths: number | null;
  consigneeLocation: string | null;
  defaultInspectionLocation: ProjectInspectionLocation | null;
  inspectionAuthority: string | null;
  remarks: string | null;
  ndcRequired: boolean;
  ndcIssuedDate: string | null;
  ndcReferenceNumber: string | null;
  ndcRemarks: string | null;
  ndcIssuedByUserId: string | null;
  partsWorkflowState: 'NOT_STARTED' | 'ENTERING' | 'PENDING_APPROVAL' | 'FINALISED';
  partsFinalisedAt: string | null;
  partsFinalisedByUserId: string | null;
  projectClosedAt: string | null;
  closureNotes: string | null;
  closedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  rowVersion: number;
  // Derived
  lifecycleState: ProjectLifecycleState;
  daysInLifecycleState: number;
  isOverdue: boolean;
  warrantyEndDate: string | null;
  contractValuePaisa: number;
  costPaisa: number;
  /** Spec §6.1 — contract value − cost. Signed integer. */
  marginPaisa: number;
}

export interface ProjectCreateInput {
  projectTitle: string;
  projectNumber: string;
  clientId: string;
  projectCurrency?: ProjectCurrencyCode;
  otherCurrencyCode?: string;
  principalName?: string;
  principalAddress?: string;
  principalContactPerson?: string;
  principalEmail?: string;
  principalPhone?: string;
  principalFax?: string;
  portOfShipment?: string;
  principalBankName?: string;
  principalBankAccountNumber?: string;
  principalBankIban?: string;
  principalBankAccountTitle?: string;
  principalBankSwiftCode?: string;
  principalBankAddress?: string;
  principalBankEmail?: string;
  principalBankPhone?: string;
  principalBankFax?: string;
  dfaReceivedDate: string;
  dfaSubmittedDate?: string;
  contractSignDate?: string;
  deliveryDurationMonths?: number;
  gracePeriodDays?: number;
  origin?: string;
  indentNumber?: string;
  indentDate?: string;
  makerNameBrand?: string;
  totalStoreValuePaisa?: number;
  gstRateBps?: number;
  requiredBgAmountPaisa?: number;
  bgSubmissionDurationDays?: number;
  bgValidityDurationMonths?: number;
  termsOfDelivery?: ProjectTermsOfDelivery;
  paymentOnINoteBps?: number;
  paymentOnCrvBps?: number;
  partialDeliveryAllowed?: boolean;
  partialPaymentAllowed?: boolean;
  partialDeliveryPaymentRemarks?: string;
  warrantyPeriodMonths?: number;
  earnestMoneyAmountPaisa?: number;
  consigneeLocation?: string;
  defaultInspectionLocation?: ProjectInspectionLocation;
  inspectionAuthority?: string;
  remarks?: string;
  ndcRequired?: boolean;
  ndcReferenceNumber?: string;
  ndcRemarks?: string;
}

export interface ProjectUpdateInput {
  projectTitle?: string;
  projectNumber?: string;
  clientId?: string;
  projectCurrency?: ProjectCurrencyCode;
  otherCurrencyCode?: string | null;
  principalName?: string | null;
  principalAddress?: string | null;
  principalContactPerson?: string | null;
  principalEmail?: string | null;
  principalPhone?: string | null;
  principalFax?: string | null;
  portOfShipment?: string | null;
  principalBankName?: string | null;
  principalBankAccountNumber?: string | null;
  principalBankIban?: string | null;
  principalBankAccountTitle?: string | null;
  principalBankSwiftCode?: string | null;
  principalBankAddress?: string | null;
  principalBankEmail?: string | null;
  principalBankPhone?: string | null;
  principalBankFax?: string | null;
  dfaReceivedDate?: string;
  dfaSubmittedDate?: string | null;
  contractSignDate?: string | null;
  deliveryDurationMonths?: number | null;
  gracePeriodDays?: number;
  origin?: string | null;
  indentNumber?: string | null;
  indentDate?: string | null;
  makerNameBrand?: string | null;
  totalStoreValuePaisa?: number | null;
  gstRateBps?: number | null;
  requiredBgAmountPaisa?: number | null;
  bgSubmissionDurationDays?: number | null;
  bgValidityDurationMonths?: number | null;
  termsOfDelivery?: ProjectTermsOfDelivery | null;
  paymentOnINoteBps?: number | null;
  paymentOnCrvBps?: number | null;
  partialDeliveryAllowed?: boolean;
  partialPaymentAllowed?: boolean;
  partialDeliveryPaymentRemarks?: string | null;
  earnestMoneyAmountPaisa?: number | null;
  earnestSubmittedDate?: string | null;
  earnestReleasedDate?: string | null;
  warrantyPeriodMonths?: number | null;
  consigneeLocation?: string | null;
  defaultInspectionLocation?: ProjectInspectionLocation | null;
  inspectionAuthority?: string | null;
  remarks?: string | null;
  ndcRequired?: boolean;
  ndcIssuedDate?: string | null;
  ndcReferenceNumber?: string | null;
  ndcRemarks?: string | null;
  rowVersion: number;
}

export interface Client {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  rowVersion: number;
}

export interface Department {
  id: string;
  clientId: string | null;
  parentDepartmentId: string | null;
  name: string;
  contactName: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  fax: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

export type DepartmentRole = 'PROCUREMENT' | 'LOGISTICS' | 'USER' | 'QA' | 'FINANCE';

export interface ProjectDepartmentContact {
  id: string;
  projectDepartmentId: string;
  name: string;
  designation: string | null;
  officialPhone: string | null;
  personalPhone: string | null;
  email: string | null;
  fax: string | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
  rowVersion: number;
}

export interface ProjectDepartmentWithContacts {
  id: string;
  projectId: string;
  departmentId: string;
  departmentRole: DepartmentRole;
  departmentName: string;
  departmentParentId: string | null;
  departmentParentName: string | null;
  contacts: ProjectDepartmentContact[];
  createdAt: string;
  updatedAt: string;
}

export const projectQueryKey = (id: string) => ['projects', id] as const;
export const projectDepartmentsKey = (id: string) => ['projects', id, 'departments'] as const;
export const clientsListKey = ['clients'] as const;
export const departmentsListKey = ['departments'] as const;

export function useProject(projectId: string) {
  return useQuery({
    queryKey: projectQueryKey(projectId),
    queryFn: ({ signal }) => apiRequest<Project>(`/api/projects/${projectId}`, { signal }),
  });
}

export function useProjectDepartments(projectId: string) {
  return useQuery({
    queryKey: projectDepartmentsKey(projectId),
    queryFn: ({ signal }) =>
      apiRequest<ProjectDepartmentWithContacts[]>(`/api/projects/${projectId}/departments`, {
        signal,
      }),
  });
}

interface PaginatedList<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export function useClientsList() {
  return useQuery({
    queryKey: clientsListKey,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<Client>>('/api/clients?pageSize=200', { signal });
      return res.items;
    },
    staleTime: 5 * 60_000,
  });
}

interface DepartmentsListOptions {
  clientId?: string;
  isActive?: boolean;
}

export function useDepartmentsList(options: DepartmentsListOptions = {}) {
  const { clientId, isActive } = options;
  return useQuery({
    queryKey: [...departmentsListKey, { clientId: clientId ?? null, isActive: isActive ?? null }],
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<Department>>('/api/departments', {
        signal,
        query: {
          pageSize: 200,
          ...(clientId ? { clientId } : {}),
          ...(typeof isActive === 'boolean' ? { isActive } : {}),
        },
      });
      return res.items;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation<Project, ApiError, ProjectCreateInput>({
    mutationFn: (body) => apiRequest<Project>('/api/projects', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

export function useUpdateProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation<Project, ApiError, ProjectUpdateInput>({
    mutationFn: (body) =>
      apiRequest<Project>(`/api/projects/${projectId}`, { method: 'PATCH', body }),
    onSuccess: (data) => {
      qc.setQueryData(projectQueryKey(projectId), data);
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

export function useCloseProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    Project,
    ApiError,
    { closureNotes?: string; rowVersion: number; acknowledgeIncompleteClosure?: boolean }
  >({
    mutationFn: (body) =>
      apiRequest<Project>(`/api/projects/${projectId}/close`, { method: 'POST', body }),
    onSuccess: (data) => {
      qc.setQueryData(projectQueryKey(projectId), data);
      void qc.invalidateQueries({ queryKey: ['visibility'] });
      void qc.invalidateQueries({ queryKey: closureReadinessKey(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Closure readiness — Pass 22 introduces the precondition check for project
// closure (spec §5.7: closure is terminal only when all parts hit stage 18,
// all batches are settled, and all BGs are released/encashed/replaced).
// The UI uses this for the closure-blockers checklist before the user
// attempts to close.
// ---------------------------------------------------------------------------

export type ClosureBlockerCode =
  | 'PROJECT_ALREADY_CLOSED'
  | 'PARTS_NOT_FINALISED'
  | 'PART_PIPELINE_INCOMPLETE'
  | 'BATCHES_OPEN'
  | 'BANK_GUARANTEES_OPEN';

export interface ClosureBlocker {
  code: ClosureBlockerCode;
  message: string;
  details?: ReadonlyArray<{ id: string; label: string }>;
}

export interface ClosureReadiness {
  canClose: boolean;
  blockers: ClosureBlocker[];
}

export const closureReadinessKey = (projectId: string) =>
  ['projects', projectId, 'closure-readiness'] as const;

export function useClosureReadiness(projectId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: closureReadinessKey(projectId),
    queryFn: ({ signal }) =>
      apiRequest<ClosureReadiness>(`/api/projects/${projectId}/closure-readiness`, { signal }),
    enabled,
  });
}

export function useReopenProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation<Project, ApiError, { rowVersion: number }>({
    mutationFn: (body) =>
      apiRequest<Project>(`/api/projects/${projectId}/reopen`, { method: 'POST', body }),
    onSuccess: (data) => {
      qc.setQueryData(projectQueryKey(projectId), data);
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}

export function useAddProjectDepartment(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    ProjectDepartmentWithContacts,
    ApiError,
    { departmentId: string; departmentRole: DepartmentRole }
  >({
    mutationFn: (body) =>
      apiRequest<ProjectDepartmentWithContacts>(`/api/projects/${projectId}/departments`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectDepartmentsKey(projectId) });
    },
  });
}

export function useRemoveProjectDepartment(projectId: string) {
  const qc = useQueryClient();
  return useMutation<null, ApiError, { projectDepartmentId: string }>({
    mutationFn: ({ projectDepartmentId }) =>
      apiRequest<null>(`/api/projects/${projectId}/departments/${projectDepartmentId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectDepartmentsKey(projectId) });
    },
  });
}

export function useAddContact(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    ProjectDepartmentContact,
    ApiError,
    {
      projectDepartmentId: string;
      name: string;
      designation?: string;
      officialPhone?: string;
      personalPhone?: string;
      email?: string;
      fax?: string;
      isPrimary?: boolean;
    }
  >({
    mutationFn: ({ projectDepartmentId, ...body }) =>
      apiRequest<ProjectDepartmentContact>(
        `/api/projects/${projectId}/departments/${projectDepartmentId}/contacts`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectDepartmentsKey(projectId) });
    },
  });
}

export function useUpdateContact(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    ProjectDepartmentContact,
    ApiError,
    {
      projectDepartmentId: string;
      contactId: string;
      name?: string;
      designation?: string | null;
      officialPhone?: string | null;
      personalPhone?: string | null;
      email?: string | null;
      fax?: string | null;
      isPrimary?: boolean;
      rowVersion: number;
    }
  >({
    mutationFn: ({ projectDepartmentId, contactId, ...body }) =>
      apiRequest<ProjectDepartmentContact>(
        `/api/projects/${projectId}/departments/${projectDepartmentId}/contacts/${contactId}`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectDepartmentsKey(projectId) });
    },
  });
}

export function useRemoveContact(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    null,
    ApiError,
    { projectDepartmentId: string; contactId: string; rowVersion: number }
  >({
    mutationFn: ({ projectDepartmentId, contactId, rowVersion }) =>
      apiRequest<null>(
        `/api/projects/${projectId}/departments/${projectDepartmentId}/contacts/${contactId}`,
        { method: 'DELETE', query: { rowVersion } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectDepartmentsKey(projectId) });
    },
  });
}
